const DEFAULT_TIMEOUT_MS = 15000;

export class ApiError extends Error {
  constructor(message, { status = 0, code = "api_error", details = null } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export class ConflictError extends ApiError {
  constructor(message, details) {
    super(message, { status: 409, code: "conflict", details });
    this.name = "ConflictError";
  }
}

const requestJson = async (url, options = {}) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), options.timeoutMs || DEFAULT_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      ...options,
      credentials: "same-origin",
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...(options.headers || {})
      }
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      throw new ApiError("The server returned an invalid response.", {
        status: response.status,
        code: "invalid_json"
      });
    }

    if (!response.ok) {
      const message = payload?.error || `Request failed with status ${response.status}.`;
      if (response.status === 409) throw new ConflictError(message, payload);
      throw new ApiError(message, {
        status: response.status,
        code: payload?.code || "http_error",
        details: payload
      });
    }

    return payload;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error?.name === "AbortError") {
      throw new ApiError("The server took too long to respond.", { code: "timeout" });
    }
    throw new ApiError("Could not reach the server.", {
      code: "network_error",
      details: error
    });
  } finally {
    clearTimeout(timeout);
  }
};

let csrfToken = "";

export const setCsrfToken = (value) => {
  csrfToken = value || "";
};

export const apiRequest = async (action, {
  method = "GET",
  query = {},
  body = null
} = {}) => {
  const params = new URLSearchParams({ action });
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });
  const payload = await requestJson(`api.php?${params.toString()}`, {
    method,
    body: body === null ? undefined : JSON.stringify(body),
    headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {}
  });
  if (payload?.csrfToken) setCsrfToken(payload.csrfToken);
  return payload;
};
